import { getUID } from '@redviking/argonaut-util/src/getUID';
import { UiFlowNodeControl } from '../../controls/UiFlowNodeControl';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import type { RequiredSockets, UiNodeSpec } from './UiNodeSpec';
import type { NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { UiFlowNodeInput } from '../../sockets/UiFlowNodeInput';

export const eventSwitchUiNodeSpec: UiNodeSpec<'eventSwitch'> = {
    requiredSockets: () => {
        const ret: ReturnType<RequiredSockets<'eventSwitch', NodeSocketKindMap['eventSwitch']>> = {
            required: [ 'event', 'condition' ],
            conditionallyRequired: [
                {
                    kind: 'or',
                    availableOptions: [ 'false', 'true' ],
                },
            ],
        };
        return ret;
    },
    cfg: () => ({
        disabled: false,
        id: getUID(),
        kind: 'eventSwitch',
        controlValues: { conditionInputs: 1, logicalOperator: 'and' },
    }),
    controls: getNodeCfg => {
        return {
            conditionInputs: {
                cfg: {
                    kind: 'button',
                    label: 'Add Input',
                    onClick: () => {
                        const nodeCfg = getNodeCfg();
                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    conditionInputs: nodeCfg.controlValues.conditionInputs + 1,
                                },
                            },
                        });
                    },
                },
                index: 4,
                nodeCfg: getNodeCfg(),
                value: getNodeCfg().controlValues.conditionInputs,
            },
            logicalOperator: {
                cfg: {
                    kind: 'selection',
                    items: [
                        { value: 'and', label: 'AND' },
                        { value: 'or', label: 'OR' },
                    ],
                    nullable: false,
                },
                nodeCfg: getNodeCfg(),
                index: 3,
                value: getNodeCfg().controlValues.logicalOperator,
            },
        };
    },
    inputs: nodeCfg => {
        const eventInput: UiFlowNodeInput.Opts<'event'> = { socket: 'event', label: 'Event', index: 1 };
        const firstConditionInput: UiFlowNodeInput.Opts<'varValue'> = { socket: 'varValue', label: 'Condition', index: 5 };

        const inputs: ReturnType<UiNodeSpec<'eventSwitch'>['inputs']> = {
            event: eventInput,
            condition: firstConditionInput,
        };
        for (let i = 1; i < nodeCfg.controlValues.conditionInputs; i++) {
            const control: UiFlowNodeControl.Opts<unknown> = {
                cfg: {
                    kind: 'deletableSocket',
                    label: 'Condition',
                    onDelete () {
                        removeNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    conditionInputs: nodeCfg.controlValues.conditionInputs - 1,
                                },
                            },
                            direction: 'input',
                            removalMethod: {
                                index: i,
                                prefix: 'input',
                                kind: 'indexedIdentifier',
                            },
                        });
                    },
                },
                nodeCfg,
                value: null,
                socketDefault: false,
            };
            inputs[`condition${i}`] = ({
                control,
                socket: 'varValue',
                index: 5,
            });
        }
        return inputs;
    },
    kindLabel: 'Event Switch',
    nodeCategory: 'events',
    outputs: () => ({
        false: {
            socket: 'event',
            label: 'False',
            index: 2,
        },
        true: {
            socket: 'event',
            label: 'True',
            index: 0,
        },
    }),
    validator: () => true,
};
